import React, { useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    List,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { Search, SearchIconWrapper, StyledInputBase } from "app/shared/SearchGlobal/style";
import _ from "lodash";
import { getAvatarInitials } from "app/utils/appHelpers";
import Div from "@jumbo/shared/Div";
import userServices from "app/services/user-services";
import dashboardServices from "app/services/dashboard-services";
import SearchIcon from "@mui/icons-material/Search";
import CustomPagination from 'app/widgets/Shared/Pagination/CustomPagination';
import { HOST } from 'app/utils/constants/paths';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UserEditModal from './UserEditModal';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import UserInviteModal from './UserInviteModal';
import UserCreateModal from './UserCreateModal';
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import companyServices from "app/services/company-services";
import { useNavigate } from "react-router-dom";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { LoadingButton } from '@mui/lab';

const formatQty = (value) => Number(value || 0).toLocaleString('es-CL');
const DRIVER_USERS_LIMIT_COLORS = Object.freeze({
    limit: '#F59E0B',
    used: '#0EA5A4',
    overBy: '#DC2626',
    remaining: '#D1D5DB'
});
const PROFILE_LABELS = Object.freeze({
    superadmin: 'Superadmin',
    customer_admin: 'Admin cliente',
    supervisor: 'Supervisor',
    planner: 'Planificador',
    seller: 'Vendedor',
    picker: 'Picker',
    reader: 'Lector',
    driver: 'Conductor',
    transportista: 'Transportista',
    warehouse: 'Bodega',
    finanzas: 'Finanzas',
    client: 'Cliente'
});

const humanizeProfileName = (profileName) => PROFILE_LABELS[profileName] || profileName;
// Misma altura que los filtros y el buscador del encabezado
const HEADER_BUTTON_SX = Object.freeze({ height: 40, px: 2, whiteSpace: 'nowrap', flexShrink: 0 });
// Los filtros multiples siempre ocupan una linea: un chip y el resto resumido en "+N"
const HEADER_FILTER_SX = Object.freeze({
    width: 230,
    flexShrink: 0,
    bgcolor: 'background.paper',
    borderRadius: 1,
    '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' },
    '& .MuiAutocomplete-input': { minWidth: '24px !important' }
});

const renderCollapsedTags = (getLabel, getKey) => (value, getTagProps) => {
    if (value.length === 0) return null;

    return [
        <Chip
            key={getKey(value[0])}
            label={getLabel(value[0])}
            size="small"
            sx={{ maxWidth: 130 }}
            {...getTagProps({ index: 0 })}
        />,
        value.length > 1 && (
            <Typography key="more" variant="body2" sx={{ mx: 0.5, whiteSpace: 'nowrap' }}>
                +{value.length - 1}
            </Typography>
        )
    ];
};

const DriverUsersLimitTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;

    return (
        <Box
            sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
            }}
        >
            {payload.map((item) => (
                <Typography key={item.name} variant="body2" sx={{ color: item.color }}>
                    {item.name}: {formatQty(item.value)}
                </Typography>
            ))}
        </Box>
    );
};

const UsersIndex = () => {
    const navigate = useNavigate();
    const { authUser } = useJumboAuth();
    const isSuperadmin = useMemo(() => _.some(authUser?.profiles, p => p.name === 'superadmin'), [authUser]);
    const profileFilterOptions = useMemo(() => {
        const profileNames = Object.keys(PROFILE_LABELS);
        return isSuperadmin ? profileNames : _.without(profileNames, 'superadmin');
    }, [isSuperadmin]);
    const canCreateUser = useMemo(() => {
        return _.some(authUser?.profiles, p => p.name === 'superadmin' || p.name === 'customer_admin' || p.name === 'supervisor');
    }, [authUser]);
    const [allCompanies, setAllCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState([]);
    const [selectedProfiles, setSelectedProfiles] = useState([]);
    // const Swal = useSwalWrapper();
    // const theme = useTheme();
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [usersList, setUsersList] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const [listLoading, setListLoading] = useState(false)
    const [currentUserId, setCurrentUserId] = useState(null)
    const [currentUserProfileCompanyId, setCurrentUserProfileCompanyId] = useState(null)
    const [openUserEditModal, setOpenUserEditModal] = useState(false)
    const [openUserInviteModal, setOpenUserInviteModal] = useState(false)
    const [openUserCreateModal, setOpenUserCreateModal] = useState(false)
    const [userInActivationToggle, setUserInActivationToggle] = useState(null)
    const [driverUsersLimitModal, setDriverUsersLimitModal] = useState({
        open: false,
        message: '',
        used: 0,
        limit: 0,
        overBy: 0
    });

    useEffect(() => {
        loadDriverUsersLimitUsage()
    }, [])

    useEffect(() => {
        if (!isSuperadmin) return;
        if (authUser?.current_company_id) {
            const currentCompany = { id: authUser.current_company_id, name: authUser.current_company?.name || `Empresa ${authUser.current_company_id}` };
            setSelectedCompanies([currentCompany]);
        }
        companyServices.getAllCompanies()
            .then(resp => {
                const companies = Array.isArray(resp) ? resp : (resp?.data || []);
                setAllCompanies(companies);
            })
            .catch(() => {});
    }, [isSuperadmin])

    const visibleProfileCompanyIds = useMemo(() => {
        if (!isSuperadmin) {
            return authUser?.current_company_id ? [Number(authUser.current_company_id)] : [];
        }

        const selectedCompanyIds = _.chain(selectedCompanies)
            .map((company) => Number(company?.id))
            .filter(Number.isFinite)
            .value();

        if (selectedCompanyIds.length > 0) {
            return selectedCompanyIds;
        }

        return authUser?.current_company_id ? [Number(authUser.current_company_id)] : [];
    }, [authUser?.current_company_id, isSuperadmin, selectedCompanies]);

    const buildUsersParams = () => {
        let par = []
        let pos = 0
        par.push(encodeURI(`q[m]=and`))
        if(searchQuery && searchQuery !== 0) {
            par.push(encodeURI(`q[g][${pos}][first_name_or_last_name_or_email_or_code_cont]=${searchQuery}`))
            pos += 1
        }

        if (isSuperadmin && selectedCompanies.length > 0) {
            selectedCompanies.forEach(c => par.push(`company_ids[]=${c.id}`))
        }

        selectedProfiles.forEach(profileName => par.push(`profile_names[]=${profileName}`))

        par.push(`page=${currentPage}`)
        par.push(`include=companies,profiles`)

        par = _.join(par, '&')

        return `?${par}`;
    }

    const usersParams = buildUsersParams()
    const [reloadToken, setReloadToken] = useState(0)
    const reloadUsers = () => setReloadToken((token) => token + 1)

    // La lista se pide cada vez que cambian los parametros; si cambian mientras una carga
    // sigue en curso, esa respuesta se descarta y vale la ultima.
    useEffect(() => {
        let stale = false
        setListLoading(true)
        userServices.getUsers(usersParams)
            .then(resp => {
                if (stale) return
                setUsersList(resp.data)
                setTotalPages(resp.meta.total_pages)
            })
            .catch(function (error) {})
            .finally(() => {
                if (!stale) setListLoading(false)
            });
        return () => { stale = true }
    }, [usersParams, reloadToken])

    const handleCompanyFilterChange = (newSelection) => {
        setSelectedCompanies(newSelection)
        setCurrentPage(1)
    }

    const handleProfileFilterChange = (newSelection) => {
        setSelectedProfiles(newSelection)
        setCurrentPage(1)
    }

    const handleSearchQueryChange = (newVal) => {
        setCurrentPage(1)
        setSearchQuery(newVal)
    }

    const handleUserChange = (newUser) => {
        setUsersList(_.map(usersList, (u) => { return u.id == newUser.id ? newUser : u }))
        loadDriverUsersLimitUsage()
    }

    const getVisibleProfileGroups = (user) => {
        const companiesById = _.keyBy(_.uniqBy(user?.companies || [], 'id'), (company) => `${company.id}`);
        const profiles = Array.isArray(user?.profiles) ? user.profiles : [];
        const filteredProfiles = visibleProfileCompanyIds.length > 0
            ? _.filter(profiles, (profile) => visibleProfileCompanyIds.includes(Number(profile?.company_id)))
            : profiles;

        return _.chain(filteredProfiles)
            .groupBy((profile) => `${profile.company_id}`)
            .map((companyProfiles, companyId) => ({
                companyId,
                companyName: companiesById[companyId]?.name || `Empresa ${companyId}`,
                profileNames: _.uniq(_.map(companyProfiles, 'name'))
            }))
            .sortBy((group) => group.companyName?.toLowerCase?.() || '')
            .value();
    }

    const resolvePreferredProfileCompanyId = (user) => {
        const userCompanyIds = _.chain(user?.companies || [])
            .map((company) => Number(company?.id))
            .filter(Number.isFinite)
            .value();

        return visibleProfileCompanyIds.find((companyId) => userCompanyIds.includes(companyId))
            || Number(user?.current_company_id)
            || userCompanyIds[0]
            || null;
    }

    const toggleUserActivation = (user) => {
        if(!user?.id) return

        setUserInActivationToggle(user.id)
        userServices.toggleUserActivation(user.id, "?include=companies,profiles")
            .then(resp => {
                handleUserChange(resp)
            })
            .catch(function (error) {})
            .finally(() => {
                setUserInActivationToggle(null)
            });
    }

    const buildDriverUsersLimitChartData = (used, limit) => {
        const safeUsed = Number(used || 0);
        const safeLimit = Number(limit || 0);
        const overBy = Math.max(safeUsed - safeLimit, 0);

        if (safeLimit > 0 && overBy > 0) {
            return [
                { name: 'Límite plan', value: safeLimit, color: DRIVER_USERS_LIMIT_COLORS.limit },
                { name: 'Sobreconsumo', value: overBy, color: DRIVER_USERS_LIMIT_COLORS.overBy }
            ];
        }

        if (safeLimit > 0) {
            return [
                { name: 'Usado', value: safeUsed, color: DRIVER_USERS_LIMIT_COLORS.used },
                { name: 'Disponible', value: Math.max(safeLimit - safeUsed, 0), color: DRIVER_USERS_LIMIT_COLORS.remaining }
            ];
        }

        return [{ name: 'Usado', value: safeUsed, color: DRIVER_USERS_LIMIT_COLORS.used }];
    };

    const closeDriverUsersLimitModal = () => {
        setDriverUsersLimitModal({ open: false, message: '', used: 0, limit: 0, overBy: 0 });
    };

    // El backend rechazo el alta o el cambio de perfil porque la compania ya llego al tope
    // de conductores del plan: se usa el mismo modal, con los numeros que manda el backend.
    const openDriverUsersLimitModalFromError = (data) => {
        const used = Number(data?.used || 0);
        const limit = Number(data?.limit || 0);

        setDriverUsersLimitModal({
            open: true,
            message: data?.error || 'Alcanzaste el limite de usuarios con perfil conductor o transportista de tu plan actual.',
            used,
            limit,
            overBy: Math.max(used - limit, 0)
        });
    };

    const loadDriverUsersLimitUsage = () => {
        dashboardServices.getSubscriptionOrderUsage()
            .then((response) => {
                const metrics = Array.isArray(response?.metrics) ? response.metrics : [];
                const driverMetric = metrics.find((metric) => metric?.feature_key === 'usuariosMobile');
                if (!driverMetric) return;

                const used = Number(driverMetric.used || 0);
                const limit = Number(driverMetric.limit || 0);
                const overBy = Number(driverMetric.over_by || 0);
                const unlimited = Boolean(driverMetric.unlimited);

                if (unlimited || overBy <= 0 || limit <= 0) return;

                setDriverUsersLimitModal({
                    open: true,
                    message: 'Alcanzaste el limite de usuarios con perfil conductor o transportista de tu plan actual.',
                    used,
                    limit,
                    overBy
                });
            })
            .catch(() => {});
    };

    const debounceSearchQueryChange = _.debounce(handleSearchQueryChange, 450)

    const renderUsersList = () => {
        if(usersList){
            return (
                <React.Fragment>
                    <UserInviteModal isOpen={openUserInviteModal} onClose={(changed, userOutput) => { setOpenUserInviteModal(false) }} onSave={() => {}} />
                    <UserCreateModal
                        isOpen={openUserCreateModal}
                        onClose={() => setOpenUserCreateModal(false)}
                        onSave={(newUser) => { reloadUsers(); loadDriverUsersLimitUsage() }}
                        onDriverLimitReached={openDriverUsersLimitModalFromError}
                    />
                    <UserEditModal
                        userID={currentUserId}
                        initialProfileCompanyId={currentUserProfileCompanyId}
                        isOpen={openUserEditModal}
                        allCompanies={allCompanies}
                        onClose={(changed, userOutput) => {
                            setOpenUserEditModal(false)
                            setCurrentUserProfileCompanyId(null)
                        }}
                        onUserChange={handleUserChange}
                        onDriverLimitReached={openDriverUsersLimitModalFromError}
                    />
                    <List disablePadding>
                        {_.map(usersList, (u, i) => {
                            const visibleProfileGroups = getVisibleProfileGroups(u);

                            return (
                                <React.Fragment key={u.id}>
                                    <Card sx={{mb: 2}}>
                                        <CardContent sx={{display: 'flex'}}>
                                            <Grid container spacing={5}>
                                                <Grid item xs={12} md={3}>
                                                    <Stack
                                                        direction="column"
                                                        justifyContent="start"
                                                        alignItems="left"
                                                        spacing={0.5}>
                                                        <Typography variant={"overline"} color="primary">{u.email}</Typography>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography variant={"button"} color="primary">{u.first_name} {u.last_name}</Typography>
                                                            <Chip
                                                                size="small"
                                                                color={u.activated === false ? "default" : "success"}
                                                                label={u.activated === false ? "Inactivo" : "Activo"}
                                                            />
                                                        </Stack>
                                                    </Stack>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <Stack
                                                                direction="column"
                                                                justifyContent="start"
                                                                alignItems="left"
                                                                spacing={0.5}>
                                                                <Typography variant={"overline"}>Empresas asociadas</Typography>
                                                                { u.companies?.length <= 0 && <Typography variant={"body"}>-</Typography> }
                                                                <AvatarGroup max={3} sx={{justifyContent: 'flex-end', cursor: 'pointer', '.MuiAvatarGroup-avatar': { backgroundColor: '#EEEEEE' }}}>
                                                                    { _.map(_.uniqBy(u.companies, 'id'), (c) => {
                                                                        return (
                                                                            <Tooltip title={c.name} key={`${u.id}_${c.id}`}>
                                                                                { c.logo_url ?
                                                                                    <Avatar src={`${HOST}${c.logo_url}`} />
                                                                                    :
                                                                                    <Avatar alt={c.name}>
                                                                                        {getAvatarInitials(c.name)}
                                                                                    </Avatar>
                                                                                }
                                                                            </Tooltip>
                                                                        )
                                                                    })}
                                                                </AvatarGroup>
                                                            </Stack>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <Stack
                                                                direction="column"
                                                                justifyContent="start"
                                                                alignItems="left"
                                                                spacing={0.5}>
                                                                <Typography variant={"overline"}>Perfiles asociados</Typography>
                                                                {visibleProfileGroups.length <= 0 && <Typography variant={"body2"}>-</Typography>}
                                                                <Stack spacing={1}>
                                                                    {_.map(visibleProfileGroups, (group) => (
                                                                        <Box key={`${u.id}_${group.companyId}`}>
                                                                            <Typography variant="caption" color="text.secondary">
                                                                                {group.companyName}
                                                                            </Typography>
                                                                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                                                {_.map(group.profileNames, (profileName) => (
                                                                                    <Chip
                                                                                        key={`${u.id}_${group.companyId}_${profileName}`}
                                                                                        label={humanizeProfileName(profileName)}
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                    />
                                                                                ))}
                                                                            </Stack>
                                                                        </Box>
                                                                    ))}
                                                                </Stack>
                                                            </Stack>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12} md={3} style={{height: '100%'}}>
                                                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                                                        <LoadingButton
                                                            loading={userInActivationToggle === u.id}
                                                            variant="contained"
                                                            size="small"
                                                            color={u.activated === false ? "success" : "warning"}
                                                            sx={{minWidth: 130}}
                                                            onClick={() => toggleUserActivation(u)}>
                                                            {u.activated === false ? "Activar" : "Desactivar"}
                                                        </LoadingButton>
                                                        <IconButton onClick={() => {
                                                            setCurrentUserId(u.id);
                                                            setCurrentUserProfileCompanyId(resolvePreferredProfileCompanyId(u));
                                                            setOpenUserEditModal(true);
                                                        }}>
                                                            <EditOutlinedIcon />
                                                        </IconButton>
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </React.Fragment>
                            )
                        })}
                    </List>
                </React.Fragment>
            )
        } else {
            return(
                <Typography>
                    No se encontraron datos
                </Typography>
            )
        }
    }

    const renderDriverUsersLimitModal = () => {
        const used = Number(driverUsersLimitModal.used || 0);
        const limit = Number(driverUsersLimitModal.limit || 0);
        const overBy = Number(driverUsersLimitModal.overBy || 0);
        const chartData = buildDriverUsersLimitChartData(used, limit);
        const chartTotal = chartData.reduce((acc, entry) => acc + Number(entry.value || 0), 0);

        return (
            <Dialog
                open={driverUsersLimitModal.open}
                onClose={closeDriverUsersLimitModal}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Limite del plan alcanzado</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body1">
                            {driverUsersLimitModal.message}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Uso actual: {formatQty(used)} / {formatQty(limit)} conductores / transportistas.
                        </Typography>
                        {overBy > 0 && (
                            <Typography variant="body2" sx={{ color: DRIVER_USERS_LIMIT_COLORS.overBy, fontWeight: 600 }}>
                                Sobreconsumo: {formatQty(overBy)} conductores / transportistas.
                            </Typography>
                        )}

                        <Box sx={{ height: 260, position: 'relative' }}>
                            {chartTotal > 0 ? (
                                <>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius={54}
                                                outerRadius={84}
                                                paddingAngle={2}
                                            >
                                                {chartData.map((entry) => (
                                                    <Cell key={entry.name} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip content={<DriverUsersLimitTooltip />} />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            pointerEvents: 'none'
                                        }}
                                    >
                                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                            {formatQty(used)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            usuarios usados
                                        </Typography>
                                    </Box>
                                </>
                            ) : (
                                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Sin datos de consumo.
                                    </Typography>
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDriverUsersLimitModal}>
                        Aceptar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            closeDriverUsersLimitModal();
                            navigate('/subscriptions');
                        }}
                    >
                        Ver suscripciones
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
    
    return (
        <div>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{mb: 2}}>
                <Typography variant="h2" sx={{mb: 0}}>Usuarios</Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}>
                    {isSuperadmin && (
                        <Autocomplete
                            multiple
                            size="small"
                            options={allCompanies}
                            getOptionLabel={(option) => option.name || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedCompanies}
                            onChange={(_, newValue) => handleCompanyFilterChange(newValue)}
                            renderInput={(params) => (
                                <TextField {...params} label="Filtrar por empresa" placeholder={selectedCompanies.length ? '' : 'Seleccionar empresas'} />
                            )}
                            renderTags={renderCollapsedTags((option) => option.name, (option) => option.id)}
                            sx={HEADER_FILTER_SX}
                            noOptionsText="No hay empresas"
                        />
                    )}
                    <Autocomplete
                        multiple
                        size="small"
                        disableCloseOnSelect
                        options={profileFilterOptions}
                        getOptionLabel={humanizeProfileName}
                        value={selectedProfiles}
                        onChange={(_, newValue) => handleProfileFilterChange(newValue)}
                        renderInput={(params) => (
                            <TextField {...params} label="Filtrar por perfil" placeholder={selectedProfiles.length ? '' : 'Seleccionar perfiles'} />
                        )}
                        renderTags={renderCollapsedTags(humanizeProfileName, (option) => option)}
                        sx={HEADER_FILTER_SX}
                        noOptionsText="No hay perfiles"
                    />
                    {canCreateUser && (
                        <Button variant="contained" color="success" sx={HEADER_BUTTON_SX} startIcon={<PersonAddAlt1OutlinedIcon/>} onClick={() => setOpenUserCreateModal(true)}>Crear usuario</Button>
                    )}
                    <Button variant="contained" color="primary" sx={HEADER_BUTTON_SX} startIcon={<ForwardToInboxOutlinedIcon/>} onClick={() => { setOpenUserInviteModal(true) }}>Invitar</Button>
                    <Search sx={{maxWidth: { sm: 200, md: 300 }}}>
                        <SearchIconWrapper>
                            <SearchIcon/>
                        </SearchIconWrapper>

                        <StyledInputBase
                            placeholder="Buscar"
                            inputProps={{'aria-label': 'search'}}
                            sx={{background: 'white'}}
                            onChange={(e) => {
                                debounceSearchQueryChange(e.target.value)
                            }}
                            onKeyDown={(e) => {
                                if (e.key == 'Enter') {
                                    handleSearchQueryChange(e.target.value)
                                }
                            }}
                        />
                    </Search>
                </Stack>
            </Stack>

            <Div sx={{display: listLoading ? 'block' : 'none', textAlign: 'center', my: 5}}>
                <CircularProgress color="warning"/>
            </Div>
            <Grid container spacing={3.75}>
                <Grid item xs={12}>
                    {renderUsersList()}
                </Grid>
            </Grid>
            <CustomPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageSelect={(page) => { setCurrentPage(page) }}/>
            {renderDriverUsersLimitModal()}
        </div>
    );
};

export default UsersIndex;
